import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { joyApi } from "../api/joyApi";
import AppShell from "../components/AppShell";
import TopBar from "../components/TopBar";
import LoadingScreen from "../components/LoadingScreen";
import ErrorState from "../components/ErrorState";

const LEVELS = [
  { level: 1, emoji: "🌸", name: "Nível 1 - Iniciante", color: "#FF69B4", chip: "#FFE0EC" },
  { level: 2, emoji: "🌻", name: "Nível 2 - Intermediário", color: "#FFB300", chip: "#FFF0C4" },
  { level: 3, emoji: "🦋", name: "Nível 3 - Avançado", color: "#5B8FB9", chip: "#DCEAF5" },
];

function LessonCard({ lesson, done, color, onClick }) {
  return (
    <button
      data-testid={`lesson-card-${lesson.id}`}
      onClick={onClick}
      className="joy-press w-full flex items-center gap-4 bg-white rounded-[22px] p-4 text-left border-2 border-[#FFEAD6] relative"
      style={{ boxShadow: "0 6px 16px rgba(0,0,0,0.06)" }}
    >
      <div
        className="w-14 h-14 rounded-[16px] flex items-center justify-center text-3xl shrink-0"
        style={{ background: `${color}22` }}
      >
        {lesson.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-black text-[#4A3728] text-lg leading-tight truncate">{lesson.title_pt}</p>
        <p className="font-bold text-[#8B7355] text-sm truncate">{lesson.title_en}</p>
        <span
          className="inline-block mt-1 text-xs font-black px-2 py-0.5 rounded-full"
          style={{ background: "#FFF3B0", color: "#8B6B00" }}
        >
          ⭐ {lesson.xp_reward} XP
        </span>
      </div>
      {done && (
        <span
          data-testid={`lesson-done-${lesson.id}`}
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#4CAF50] text-white flex items-center justify-center text-sm font-black joy-pop"
        >
          ✓
        </span>
      )}
    </button>
  );
}

export default function Lessons() {
  const nav = useNavigate();
  const { userId } = useAuth();
  const lessonsQ = useQuery({ queryKey: ["lessons"], queryFn: joyApi.lessons });
  const progressQ = useQuery({
    queryKey: ["progress", userId],
    queryFn: () => joyApi.progress(userId),
    enabled: !!userId,
  });

  if (lessonsQ.isLoading || progressQ.isLoading) return <LoadingScreen />;

  const completed = progressQ.data?.completed_lessons || [];
  const lessons = lessonsQ.data || [];

  return (
    <AppShell>
      <TopBar title="Lições 📚" testId="lessons-topbar" to="/" />
      {lessonsQ.isError ? (
        <ErrorState onRetry={lessonsQ.refetch} />
      ) : (
        <>
          <div className="bg-white rounded-[22px] p-4 flex items-start gap-3 border-2 border-[#FFE0EC] mb-5 joy-slide-in">
            <img src="/joy.png" alt="Joy" className="w-12 h-12 rounded-full object-cover border-2 border-[#FF69B4]" />
            <p className="font-bold text-[#4A3728] text-sm leading-snug">
              🦊 Aprenda inglês comigo!<br />Toque em uma lição para começar! 💕
            </p>
          </div>

          {LEVELS.map((lv) => {
            const group = lessons.filter((l) => l.level === lv.level).sort((a, b) => a.order - b.order);
            if (!group.length) return null;
            return (
              <div key={lv.level} className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">{lv.emoji}</span>
                  <h2 className="font-black text-lg" style={{ color: lv.color }}>{lv.name}</h2>
                </div>
                <div className="flex flex-col gap-3">
                  {group.map((lesson) => (
                    <LessonCard
                      key={lesson.id}
                      lesson={lesson}
                      color={lv.color}
                      done={completed.includes(lesson.id)}
                      onClick={() => nav(`/lesson/${lesson.id}`)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </AppShell>
  );
}
